import { Field, useFormikContext } from 'formik';
import type { FC } from 'react';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import useFocusOnRouteChange from '../../hooks/useFocusOnRouteChange';
import { ListQuery } from '../../models/ListQuery';
import { MultiSelectOption } from '../../models/MultiSelectOption';
import { getBuildingNamesWithPagination } from '../../services/buildingService';
import { autoCreateFloorsForBuilding } from '../../services/floorService';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import ActivityFrame from '../activity-frame/activity-frame';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';

interface BuildingStepProps {
    onNext: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onBack: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    setStepDescriptions: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

const BuildingStep: FC<BuildingStepProps> = ({ onNext, onBack, setStepDescriptions }) => {
    const { translate } = useLocale();
    const { values, errors, setFieldValue } = useFormikContext<any>();
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const inputRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = React.useState<boolean>(false);
    const [showWarning, setShowWarning] = React.useState<boolean>(false);

    useFocusOnRouteChange(inputRef);

    const onLocalNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (showWarning) {
            setStepDescriptions(prev => ({ ...prev, step2: values.buildingName }));
            onNext(e);
            return;
        }

        setBusy(true);
        autoCreateFloorsForBuilding(values.buildingId, values.startingFloor, values.lastFloor).then(() => {
            setStepDescriptions(prev => ({ ...prev, step2: values.buildingName }));
            onNext(e);
        }).catch(() => {
            setShowWarning(true);
        }).finally(() => {
            setBusy(false);
        });
    }

    const isStepValid = () => {
        return !errors.buildingId && !errors.startingFloor && !errors.lastFloor && values.buildingId && values.startingFloor !== '' && values.lastFloor !== '';
    }

    return <ActivityFrame busy={busy} busyMessage={translate.byKey(TranslationKeys.autoCreatingFloors)} className={`flex flex-col h-full w-full ${busy ? ' justify-center' : 'px-2 py-1 relative space-y-1'}`}>
        <span className={`text-portal-blue-primary-shade2 text-xl font-semibold`} >
            {translate.byKey(TranslationKeys.selectBuilding)}
        </span>
        {/* Building selection */}
        <div className='flex flex-col'>
            <div className="flex flex-row justify-between" id='nameLabel'>
                <Label htmlFor='buildingId'
                    className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.building)}:</Label>
                {errors.buildingId ? <span className='text-red-600 text-xs'>{errors.buildingId}</span> : null}
            </div>
            <PaginatedSearchableSelectField
                name={translate.byKey(TranslationKeys.building)}
                key={"buildingId"}
                accessor='buildingId'
                dataMethod={(listParameters?: ListQuery) => getBuildingNamesWithPagination({ ...listParameters, filters: appendFilter(values.siteId ?? siteId, 'Site id', 'site.id', listParameters?.filters ?? []) })}
                searchAccessor={'buildingName'}
                onSelect={(_, item: MultiSelectOption) => {
                    setFieldValue('buildingName', item?.label);
                    setFieldValue('buildingId', item?.value);
                }}
                selectedItemLabel={values.buildingName}
                dataCy='buildingField' />
        </div>
        {/* Starting floor */}
        <div className='flex flex-col'>
            <div className="flex flex-row justify-between" id='startingFloorLabel'>
                <Label
                    htmlFor="startingFloor"
                    className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.startingFloor)}:</Label>
                {errors.startingFloor ? <span className='text-red-600 text-xs'>{errors.startingFloor}</span> : null}
            </div>

            <Field
                type="number"
                name="startingFloor"
                data-cy="startingFloorField"
                id="startingFloorInput"
                innerRef={inputRef}
                className={`text-gray-800 flex flex-row items-center pr-2 pl-2 p-1 text-sm w-full bg-gray-input`}/>
        </div>
        {/* Last Floor */}
        <div className='flex flex-col'>
            <div className="flex flex-row justify-between" id='lastFloorLabel'>
                <Label
                    htmlFor="lastFloor"
                    className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.lastFloor)}:</Label>
                {errors.lastFloor ? <span className='text-red-600 text-xs'>{errors.lastFloor}</span> : null}
            </div>

            <Field
                type="number"
                name="lastFloor"
                data-cy="lastFloorField"
                id="lastFloorInput"
                innerRef={inputRef}
                className={`text-gray-800 bg-gray-input text-sm w-full flex flex-row items-center pr-2 pl-2 p-1`}/>
        </div>
        <div className={`flex flex-col pt-3 ${showWarning ? '' : 'hidden'}`} id='failedFloorAutoCreateWarningId'>
            <span className='text-orange-600 font-semibold'>{translate.byKey(TranslationKeys.warning)}</span>
            <span className={`text-xs`} >
                {translate.byKey(TranslationKeys.failedFloorAutoCreateWarning)}
            </span>
        </div>
        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            <div className="flex flex-row mt-4">
                <button
                    className="secondary secondary-inverse py-1 px-5"
                    type="button"
                    data-cy="backButton"
                    onClick={onBack}>{translate.byKey(TranslationKeys.back)}
                </button>
            </div>
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    className={`py-1 px-5 secondary ${isStepValid() ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                    disabled={!isStepValid()}
                    type="button"
                    data-cy="nextButton"
                    onClick={onLocalNext}>{translate.byKey(TranslationKeys.next)}
                </button>
            </div>
        </div>
    </ActivityFrame>
}

export default BuildingStep;